/* eslint-disable react/require-default-props */
import * as React from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Slide from '@mui/material/Slide';
import ModalHeader from './ModalHeader';
import { cx } from '../../helpers/global';
import ModalBottomButtons from './ModalBottomButtons';
import './modal.css';

interface ModalHeaderSlots {
  badge: HTMLDivElement | null;
  aside: HTMLDivElement | null;
}

const ModalHeaderSlotsContext = React.createContext<ModalHeaderSlots>({
  badge: null,
  aside: null,
});

/**
 * Renders beside the modal's close button. The header sits above the modal's
 * children, so a child that owns what it should say puts it here rather than
 * the type being lifted out of the form that knows it.
 *
 * The plain variant drops the pill for a label that reads as status rather
 * than as a name for what is being created.
 */
export function ModalHeaderBadge({
  label,
  variant = 'pill',
}: {
  label: string;
  variant?: 'pill' | 'plain';
}) {
  const { badge } = React.useContext(ModalHeaderSlotsContext);
  if (!badge) return null;
  return createPortal(
    <Box className={cx('modal-badge', variant === 'plain' && 'plain')}>
      {variant === 'pill' && (
        <Box component="span" className="modal-badge-dot" />
      )}
      {label}
    </Box>,
    badge,
  );
}

/** The same slot mechanism, opposite the badge. */
export function ModalHeaderAside({ children }: React.PropsWithChildren) {
  const { aside } = React.useContext(ModalHeaderSlotsContext);
  if (!aside) return null;
  return createPortal(children, aside);
}

const style = {
  position: 'fixed' as 'fixed',
  top: '0px',
  right: '0px',
  width: '100%',
  bgcolor: '#fff',
  overflowY: 'scroll',
  height: '100dvh',
};

interface Props {
  modalOpen: boolean;
  setModalOpen:
    | React.Dispatch<React.SetStateAction<boolean>>
    | ((open: boolean) => void);
  name: string;
  children: React.ReactNode;
  handleClose?: () => void;
  shouldScrollTop?: boolean;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  large?: boolean;
  submitButton?: React.ComponentProps<typeof ModalHeader>['submitButton'];
  secondaryButton?: React.ComponentProps<typeof ModalHeader>['secondaryButton'];
  // Return false to keep the modal open (e.g. unsaved-changes confirmation)
  confirmClose?: () => Promise<boolean> | boolean;
}

export default function FullPageModal({
  modalOpen,
  setModalOpen,
  name,
  children,
  handleClose,
  shouldScrollTop,
  setShouldScrollToTop,
  large = false,
  submitButton,
  secondaryButton,
  confirmClose,
}: Props) {
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  const [badgeSlot, setBadgeSlot] = React.useState<HTMLDivElement | null>(null);
  const [asideSlot, setAsideSlot] = React.useState<HTMLDivElement | null>(null);
  // Memoized so re-rendering the modal does not re-render every header portal.
  const headerSlots = React.useMemo(
    () => ({ badge: badgeSlot, aside: asideSlot }),
    [badgeSlot, asideSlot],
  );

  const closeModal = React.useCallback(async () => {
    if (confirmClose && !(await confirmClose())) return;
    if (handleClose) {
      handleClose();
    } else {
      setModalOpen(false);
    }
  }, [confirmClose, handleClose, setModalOpen]);

  function scrollToTop() {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
    if (setShouldScrollToTop) {
      setShouldScrollToTop(false);
    }
  }

  React.useEffect(() => {
    if (shouldScrollTop) {
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScrollTop]);

  const className = cx('full-page-modal-content', large && 'large');

  // Backdrop/Escape only close for callers that opt in via either prop
  const onClose = handleClose || confirmClose ? closeModal : undefined;

  return (
    <ModalHeaderSlotsContext.Provider value={headerSlots}>
      <Modal
        open={modalOpen}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        closeAfterTransition
        className="full-page">
        <Slide in={modalOpen} direction="up" mountOnEnter unmountOnExit>
          <Box sx={style} ref={modalContentRef}>
            <ModalHeader
              name={name}
              handleClose={closeModal}
              submitButton={submitButton}
              secondaryButton={secondaryButton}
              badgeRef={setBadgeSlot}
              asideRef={setAsideSlot}
            />
            <Box className={className}>{children}</Box>
            <ModalBottomButtons
              submitButton={submitButton}
              secondaryButton={secondaryButton}
            />
          </Box>
        </Slide>
      </Modal>
    </ModalHeaderSlotsContext.Provider>
  );
}
